import React, { useState, useEffect } from 'react';
import { History, User, Clock, ChevronRight, ChevronDown, Eye, ChevronLeft } from 'lucide-react';

import { templateAPI } from '../../services/api';

const HistorySidebar = ({ isOpen, onClose, template_content, onLoadVersion }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(true);

  useEffect(() => {
    if (isOpen && template_content?.id) {
      fetchTemplateHistory();
    }
  }, [isOpen, template_content?.id]);

  const fetchTemplateHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await templateAPI.getHistory(template_content.id);
      if (response.data.success) {
        const historyData = response.data.data.history || [];
        setHistory(historyData);
        
        // Expand all items by default
        if (historyData.length > 0) {
          const allIds = new Set(historyData.map(item => item.id));
          setExpandedItems(allIds);
        }
      } else {
        setError(response.data.message || 'Failed to fetch template history');
      }
    } catch (error) {
      console.error('Error fetching template history:', error);
      setError('Failed to load template history');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    try {
      // Ensure the date is treated as UTC if it doesn't have timezone info
      let date;
      if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-')) {
        // Already has timezone info
        date = new Date(dateString);
      } else {
        // Assume UTC if no timezone info
        date = new Date(dateString + 'Z');
      }
      
      // Add 5:30 hours (5.5 hours = 5 hours 30 minutes)
      const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
      
      return istDate.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const getRelativeTime = (dateString) => {
    try {
      // Ensure the date is treated as UTC if it doesn't have timezone info
      let date;
      if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-')) {
        // Already has timezone info
        date = new Date(dateString);
      } else {
        // Assume UTC if no timezone info
        date = new Date(dateString + 'Z');
      }
      
      // Add 5:30 hours (5.5 hours = 5 hours 30 minutes)
      const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
      const now = new Date();
      const diffInMinutes = Math.floor((now - istDate) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours}h ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays}d ago`;
      
      return formatDate(dateString);
    } catch {
      return 'Unknown time';
    }
  };

  const getJsonStats = (template_content) => {
    try {
      if (!template_content) return { size: '0 B' };
      
      const jsonString = JSON.stringify(template_content);
      const sizeInBytes = new Blob([jsonString]).size;
      
      // Format size
      let size = '';
      if (sizeInBytes < 1024) {
        size = `${sizeInBytes} B`;
      } else if (sizeInBytes < 1024 * 1024) {
        size = `${(sizeInBytes / 1024).toFixed(1)} KB`;
      } else {
        size = `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
      }
      
      return { size };
    } catch (error) {
      return { size: '0 B' };
    }
  };

  const handleViewVersion = (historyItem) => {
    // Replace current template_content with the history item's template_content
    if (historyItem.template_content && onLoadVersion) {
      const confirmed = window.confirm(
        `Load version from ${formatDate(historyItem.createdAt)}? This will replace your current design.`
      );
      
      if (confirmed) {
        onLoadVersion(historyItem.template_content);
        onClose(); // Close the history sidebar after loading
      }
    } else if (historyItem.template_content) {
      alert(`Loading version from ${formatDate(historyItem.createdAt)}. This will replace your current design.`);
      console.log('Loading version:', historyItem.id, historyItem.template_content);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed left-16 top-0 h-full w-80 bg-white border-r border-gray-200 shadow-lg z-500 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold">History</h2>
        </div>
        <History className="w-5 h-5 text-gray-500" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading history...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-red-500 mb-4">
                <History className="w-12 h-12 mx-auto" />
              </div>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={fetchTemplateHistory}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : history.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No data available</p>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {history.map((item, index) => (
              <div
                key={item.id}
                className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {item.user?.name || 'Unknown User'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {getRelativeTime(item.createdAt)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleExpanded(item.id)}
                    className="p-1 hover:bg-gray-200 rounded"
                  >
                    {expandedItems.has(item.id) ? (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                </div>

                {/* Expanded Content */}
                {expandedItems.has(item.id) && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Clock className="w-4 h-4" />
                        <span>{formatDate(item.createdAt)}</span>
                      </div>
                      
                      {/* JSON Stats */}
                      {(() => {
                        const stats = getJsonStats(item.template_content);
                        return (
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Configuration Size:</span> {stats.size}
                          </div>
                        );
                      })()}

                      {/* Actions */}
                      <div className="flex space-x-2 pt-2">
                        <button
                          onClick={() => handleViewVersion(item)}
                          className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          <span>View</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistorySidebar;
